
import { useState, useEffect } from "react";
import { addHours, addDays, differenceInHours, differenceInDays } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface UseDeadlineTimerProps {
  initialDeadline: Date | null;
  onDeadlineChange: (deadline: Date | null) => void;
}

export function useDeadlineTimer({ initialDeadline, onDeadlineChange }: UseDeadlineTimerProps) {
  const [enabled, setEnabled] = useState(!!initialDeadline);
  const [timeValue, setTimeValue] = useState("24");
  const [timeUnit, setTimeUnit] = useState<"hours" | "days">("hours");
  const [inputError, setInputError] = useState<string | null>(null);
  const { toast } = useToast();

  // Initialize from deadline
  useEffect(() => {
    console.log('useDeadlineTimer: initialDeadline changed:', initialDeadline);
    if (initialDeadline) {
      initializeFromDeadline(initialDeadline);
    }
  }, [initialDeadline]);

  // Function to initialize state from deadline
  const initializeFromDeadline = (deadline: Date | null) => {
    if (deadline) {
      const now = new Date();
      const hoursDiff = differenceInHours(deadline, now);
      
      if (hoursDiff > 48) {
        const daysDiff = differenceInDays(deadline, now);
        setTimeValue(Math.max(1, daysDiff).toString());
        setTimeUnit("days");
      } else {
        setTimeValue(Math.max(1, hoursDiff).toString());
        setTimeUnit("hours");
      }
      setEnabled(true);
    } else {
      setTimeValue("24");
      setTimeUnit("hours");
      setEnabled(false);
    }
  };

  // Handle enabling/disabling the deadline
  const handleSwitchChange = (checked: boolean) => {
    console.log('useDeadlineTimer: switch changed to:', checked);
    setEnabled(checked);
    
    if (checked) {
      updateDeadline(timeValue, timeUnit);
    } else {
      setInputError(null);
      onDeadlineChange(null);
      console.log('useDeadlineTimer: deadline disabled, set to null');
    }
  };

  // Validate and update time value on input change
  const handleTimeChange = (value: string) => {
    if (value === "" || /^\d*$/.test(value)) {
      setTimeValue(value);
      setInputError(null);
    }
  };

  // Create a new deadline based on time value and unit
  const updateDeadline = (value: string, unit: "hours" | "days") => {
    if (!enabled) return;
    
    if (value === "") {
      setInputError("Please enter a value");
      return;
    }
    
    const numValue = Number(value);

    if (unit === "hours") {
      if (numValue < 1) {
        setInputError("Minimum 1 hour");
        return;
      } else if (numValue > 720) {
        setInputError("Maximum 720 hours");
        return;
      }
    } else {
      if (numValue < 1) {
        setInputError("Minimum 1 day");
        return;
      } else if (numValue > 365) {
        setInputError("Maximum 365 days");
        return;
      }
    }

    setInputError(null);

    const newDeadline = unit === "hours" 
      ? addHours(new Date(), numValue) 
      : addDays(new Date(), numValue);
    
    console.log('useDeadlineTimer: updating deadline to:', newDeadline);
    onDeadlineChange(newDeadline);
    
    toast({
      title: "Deadline updated",
      description: `RSVP deadline set to ${format(newDeadline, "MMM d, yyyy 'at' h:mm a")}`,
    });
  };

  // Update deadline when input loses focus (user finished editing)
  const handleInputBlur = () => {
    if (enabled && timeValue) {
      updateDeadline(timeValue, timeUnit);
    }
  };

  // Convert between hours and days
  const convertValue = (value: string, fromUnit: "hours" | "days", toUnit: "hours" | "days"): string => {
    if (value === "") return value;
    
    const numValue = Number(value);
    
    if (fromUnit === "hours" && toUnit === "days") {
      // Convert hours to days (round up to nearest whole day)
      return Math.ceil(numValue / 24).toString();
    } else if (fromUnit === "days" && toUnit === "hours") {
      // Convert days to hours
      return (numValue * 24).toString();
    }
    
    return value;
  };

  // Handle unit change (hours/days) with conversion
  const handleUnitChange = (unit: "hours" | "days") => {
    if (unit === timeUnit) return;
    
    console.log('useDeadlineTimer: unit changing from', timeUnit, 'to', unit);
    
    // Convert the value if needed
    const newValue = convertValue(timeValue, timeUnit, unit);
    console.log(`Converting ${timeValue} ${timeUnit} to ${newValue} ${unit}`);
    
    // Update the UI state first
    setTimeUnit(unit);
    setTimeValue(newValue);
    setInputError(null);
  };

  return {
    enabled,
    timeValue,
    timeUnit,
    inputError,
    handleSwitchChange,
    handleTimeChange,
    handleInputBlur,
    handleUnitChange,
    eventDeadline: initialDeadline
  };
}
